'use client';

/**
 * The journey: the five steps of designing a system, told as the visitor
 * scrolls, with one project building itself beside them (JourneyStage).
 *
 * It plays on a stage (Stage.tsx) it shares with the listening room: the
 * steps build a system, and the section after them plays it, in one
 * continuous space. A pool of light rides with the picture and moves as the
 * steps do.
 *
 * The step in the middle of the screen is the current one — a thin band
 * across the viewport's centre decides it, so the picture changes when a step
 * reaches the reader's eye line rather than when its first pixel appears. The
 * current step's number wears the accent, which is the accent's one job:
 * where you are in the flow (design-language.md §1). A rail beside the list
 * fills to the same point.
 *
 * On a phone the picture sits sticky at the top with the current step
 * captioned under it, and the list is the scroll runway; on a wider screen the
 * picture rides beside the steps, held at the eye line.
 */

import { useEffect, useRef, useState } from 'react';
import type { ShowcaseProduct } from '@/lib/homeShowcase';
import { HOME_JOURNEY, HOME_JOURNEY_LABEL, type JourneyStep } from '@/lib/copy/homepage';
import JourneyStage from './JourneyStage';

export default function Journey({ system }: { system: readonly ShowcaseProduct[] }) {
  const [active, setActive] = useState(0);
  const [entered, setEntered] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const stepRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const steps = stepRefs.current.filter((el): el is HTMLLIElement => el !== null);
    const band = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(Number((e.target as HTMLElement).dataset.step));
        }
      },
      // The middle tenth of the viewport: the reader's eye line.
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );
    steps.forEach((el) => band.observe(el));

    const section = sectionRef.current;
    const arrive = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setEntered(true);
          arrive.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    if (section) arrive.observe(section);
    return () => { band.disconnect(); arrive.disconnect(); };
  }, []);

  const last = HOME_JOURNEY.length - 1;

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      data-testid="home-journey"
      className="relative scroll-mt-24"
    >
      <div className="relative mx-auto max-w-[1200px] px-5 pt-20 sm:px-8 md:pt-28">
        <p className="text-overline font-semibold uppercase text-[var(--rt-ink-dim)]">{HOME_JOURNEY_LABEL}</p>

        <div className="mt-6 grid gap-8 md:mt-0 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-14">
          {/* The picture: sticky above the steps on a phone, with the current
              step captioned under it; beside them on a wider screen, centred
              in the viewport the whole way down. The light rides with it. */}
          <div className="sticky top-[4.75rem] z-10 self-start md:top-0 md:order-2 md:flex md:h-dvh md:items-center">
            <div className="relative w-full">
              <div
                aria-hidden
                data-step-light={active}
                className="journey-light pointer-events-none absolute -inset-[18%] -z-10"
              />
              <JourneyStage step={active} entered={entered} system={system} />
              {/* A phone has no room beside the picture, and a step scrolling
                  under a sticky picture is a step nobody reads — so the
                  caption rides with the picture and the list below becomes
                  the scroll runway. The list stays the accessible copy; this
                  is its visual echo. Keyed, so each step rises in. No ground
                  of its own: the list under it is visually hidden on a phone,
                  so nothing passes behind it, and a fill would cut a box out
                  of the light the picture sits in. */}
              <div aria-hidden data-testid="journey-caption" className="pb-2 pt-5 md:hidden">
                <div key={active} className="rt-rise-in flex gap-4">
                  <StepNumber n={active + 1} current />
                  <StepText step={HOME_JOURNEY[active]} />
                </div>
              </div>
            </div>
          </div>

          {/* A quarter-screen of run-in above the first step and after the
              last: the picture is centred in a screen-tall sticky box, and it
              can only sit centred beside a step with that much room around
              it — without it the first and last steps read off-centre. */}
          <div className="relative md:order-1 md:py-[25vh]">
            {/* The rail: how far through the story the reader is, from the
                first number to the last (run-in plus half a step each end). */}
            <div aria-hidden className="absolute bottom-[50vh] left-[19px] top-[50vh] hidden w-px bg-[var(--app-border)] md:block">
              <div
                className="h-full w-full origin-top bg-[var(--rt-accent)] transition-transform duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)] motion-reduce:transition-none"
                style={{ transform: `scaleY(${active / last})` }}
              />
            </div>
            <ol className="relative">
              {HOME_JOURNEY.map((s, i) => {
                const current = i === active;
                return (
                  <li
                    key={s.title}
                    ref={(el) => { stepRefs.current[i] = el; }}
                    data-step={i}
                    data-current={current}
                    aria-current={current ? 'step' : undefined}
                    className="flex min-h-[45vh] md:min-h-[50vh] md:items-center"
                  >
                    <div className="flex gap-6 max-md:sr-only">
                      <StepNumber n={i + 1} current={current} />
                      <div
                        className="transition-[opacity,translate] duration-[var(--rt-spring-ms)] ease-[var(--rt-ease-spring)] motion-reduce:transition-none"
                        style={{ opacity: current ? 1 : 0.32, translate: current ? '0 0' : '0 6px' }}
                      >
                        <StepText step={s} heading />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/** A step's number. The current one wears the accent: position in the flow. */
function StepNumber({ n, current }: { n: number; current: boolean }) {
  return (
    <span
      className={
        'mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-body font-semibold '
        + 'bg-[var(--app-bg)] transition-colors duration-[var(--rt-fade-ms)] motion-reduce:transition-none '
        + (current
          ? 'border-[var(--rt-accent)] text-[var(--rt-accent-ink)]'
          : 'border-[var(--app-border)] text-[var(--rt-ink-dim)]')
      }
    >
      {n}
    </span>
  );
}

/** A step's title and line — a heading in the list, plain text in the
 *  phone caption that echoes it. */
function StepText({ step, heading = false }: { step: JourneyStep; heading?: boolean }) {
  const Title = heading ? 'h3' : 'p';
  return (
    <div>
      <Title className="text-display-4 font-bold text-balance text-[var(--rt-ink)]">{step.title}</Title>
      <p className="mt-2 max-w-md text-lead text-[var(--rt-ink-dim)] md:mt-3">{step.line}</p>
    </div>
  );
}
